import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import { Skeleton } from "@mui/material";
import InlineButton from "components/shared/InlineButton";
import PenEditButton from "components/shared/PenEditButton";
import StyledButton from "components/shared/StyledButton";
import DocoonSignModal from "components/shared/DocoonSignModal";
import type { CoBeneficiarySigner } from "components/shared/DocoonSignModal/DocoonSignatoryStep";
import type { DocoonSignatoryWithRole } from "store/agreementsStore";
import { useAgreementPageContext } from "containers/ContractValidationEdit/context/StateProvider";
import { BodyHighlight, SmallBody, TinyBody } from "helpers/TypographyComponents";
import { useAbility } from "hooks/useAbility";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Agreement, AgreementSigner } from "store/agreementsStore";
import {
    AgreementSignatureStatus,
    fetchAgreementDocoonSignatories,
    resendAgreement,
    saveAgreementDocoonSignatories,
    toDocoonSignatoriesPayload,
} from "store/agreementsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";

import EditPublisherSigner from "./EditPublisherSigner";
import InlineSigner from "./InlineSigner";

interface IPanelSigners {
    agreement: Agreement;
    /**
     * Related fetct term or article status
     */
    relatedResourceStatus: TFetchStatus;
}

const PanelSigners = ({ agreement, relatedResourceStatus }: IPanelSigners) => {
    const { userData } = useAppSelector((state) => state.settings);
    const { hasAbilityToEditPublisherSigner } = useAgreementPageContext();
    const hasDocoonIntegration = useAppSelector((state) => state.integrations.hasDocoonIntegration);
    const [showDocoonModal, setShowDocoonModal] = useState(false);
    const [docoonSignatories, setDocoonSignatories] = useState<DocoonSignatoryWithRole[]>([]);

    const loadDocoonSignatories = async () => {
        const res = await dispatch(fetchAgreementDocoonSignatories({ agreementId: agreement.id as number }));
        if (!fetchAgreementDocoonSignatories.fulfilled.match(res)) return;
        const sorted = [...res.payload].sort((a, b) => b.isMainSignatory - a.isMainSignatory);
        setDocoonSignatories(
            sorted.map((r) => ({
                id: r.docoonId,
                firstName: r.firstName,
                lastName: r.lastName,
                email: "",
                modeSecure: null,
                identType: null,
                identCode: null,
                beneficiaryId: r.beneficiaryId,
                isInternal: !!r.isInternalSignatory,
                isInformed: !!r.isInformed,
            })),
        );
    };

    useEffect(() => {
        if (!hasDocoonIntegration || !agreement.id) return;
        loadDocoonSignatories();
    }, [agreement.id, hasDocoonIntegration]);

    const myUserId = userData?.id ?? -1;
    const ability = useAbility();

    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const hasSent = agreement?.signedStatus === AgreementSignatureStatus.SENT;
    const isSigned = agreement?.signedStatus === AgreementSignatureStatus.SIGNED;
    const { signers } = agreement;

    const beneficiarySigners = (signers as AgreementSigner[]).filter(
        (signer) => signer.relatedResource === "beneficiary",
    );
    const mainSignerEmail =
        beneficiarySigners.find((signer) => +signer.relatedResourceId === +agreement.beneficiaryId)?.email ?? "";
    const docoonCoBeneficiaries: CoBeneficiarySigner[] = beneficiarySigners
        .filter((signer) => +signer.relatedResourceId !== +agreement.beneficiaryId)
        .map((signer) => ({ beneficiaryId: signer.relatedResourceId, email: signer.email ?? "", name: signer.name }));

    const signersIds = (signers as AgreementSigner[]).map((signer) => signer.relatedResourceId);
    const signerMe = (signers as AgreementSigner[])?.find((signer) => signer.relatedResourceId == myUserId);

    const mySignatureUrl = signersIds.includes(myUserId) ? signerMe?.signatureUrl : undefined;
    const mySignatureStatus = signersIds.includes(myUserId) ? signerMe?.signedStatus : undefined;

    const showResendSignatureBtn = hasSent && !isSigned && agreement.signProvider !== "docoon";

    const handleResendAgreement = () => {
        dispatch(resendAgreement({ agreementId: agreement?.id as number }));
    };

    const handleSign = () => {
        if (!mySignatureUrl) return;
        window.open(mySignatureUrl, "_blank");
    };

    return (
        <>
            <DocoonSignModal
                open={showDocoonModal}
                agreementId={agreement.id}
                onClose={() => setShowDocoonModal(false)}
                onSuccess={(sigs) => {
                    setDocoonSignatories(sigs);
                    dispatch(
                        saveAgreementDocoonSignatories({
                            agreementId: agreement.id as number,
                            signatories: toDocoonSignatoriesPayload(sigs),
                        }),
                    );
                }}
                initialSignatories={docoonSignatories}
                mainSignerEmail={mainSignerEmail}
                coBeneficiaries={docoonCoBeneficiaries}
            />
            {relatedResourceStatus !== "succeeded" ? (
                <Skeleton sx={{ marginBottom: "1rem" }} />
            ) : (
                <>
                    <div className="contract-validation-edit-right-panel-signers">
                        <BodyHighlight
                            color="black"
                            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                        >
                            {t("ContractValidationEdit.Signatories")}
                            {showResendSignatureBtn && (
                                <InlineButton onClick={handleResendAgreement} icon={<></>}>
                                    <SmallBody color="info" style={{ fontSize: "11px" }}>
                                        {t("ContractValidationEdit.ResendSignature")}
                                    </SmallBody>
                                </InlineButton>
                            )}
                            {hasAbilityToEditPublisherSigner && !hasDocoonIntegration && <EditPublisherSigner />}
                            {hasAbilityToEditPublisherSigner && hasDocoonIntegration && (
                                <PenEditButton
                                    onClick={async () => {
                                        if (agreement.id) await loadDocoonSignatories();
                                        setShowDocoonModal(true);
                                    }}
                                    tooltipText={t("ContractValidationEdit.EditPublisherSigner")}
                                />
                            )}
                        </BodyHighlight>
                        {docoonSignatories.length > 0
                            ? docoonSignatories.map((s, i) => (
                                  <div
                                      key={`docoon-signer-${s.id}`}
                                      className="contract-validation-edit-right-panel-label"
                                  >
                                      <SmallBody>
                                          {s.firstName} {s.lastName}
                                      </SmallBody>
                                      <div className="flex items-center gap-1">
                                          {(i === 0 || s.isInternal || s.isInformed) && (
                                              <TinyBody
                                                  color="grey"
                                                  style={{
                                                      fontWeight: "500",
                                                      fontSize: "10px",
                                                      background: "#f3f4f6",
                                                      borderRadius: "999px",
                                                      padding: "1px 6px",
                                                  }}
                                              >
                                                  {t(
                                                      i === 0
                                                          ? "DocoonSignModal.MainBadge"
                                                          : s.isInformed
                                                            ? "DocoonSignModal.InformedBadge"
                                                            : "DocoonSignModal.InternalBadge",
                                                  )}
                                              </TinyBody>
                                          )}
                                          <TinyBody color="grey" style={{ fontWeight: "300" }}>
                                              #{s.id}
                                          </TinyBody>
                                      </div>
                                  </div>
                              ))
                            : (agreement?.signers as AgreementSigner[]).map((signer) => (
                                  <InlineSigner key={`signer-${signer.relatedResourceId}`} signer={signer} />
                              ))}
                    </div>
                    <div className="contract-validation-edit-right-panel-dividers" />
                    {mySignatureUrl &&
                        mySignatureStatus === AgreementSignatureStatus.SENT &&
                        ability.can("sign", "Agreement") && (
                            <StyledButton
                                buttonType="outlined"
                                fullWidth
                                onClick={handleSign}
                                style={{ marginBottom: "0.25rem" }}
                            >
                                <DrawOutlinedIcon style={{ height: "16px", width: "16px" }} />
                                {t("ContractValidationEdit.Sign")}
                            </StyledButton>
                        )}
                </>
            )}
        </>
    );
};

export default PanelSigners;
